import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Alert,
  AlertTitle,
  Tooltip,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import BoltIcon from '@mui/icons-material/Bolt';
import GroupIcon from '@mui/icons-material/Group';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ImageIcon from '@mui/icons-material/Image';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import { useApp } from '../../context/AppContext';
import { USER_GROUP } from '../../models/types';
import { useNavigate, useLocation } from 'react-router-dom';
import { formatDate } from '../../utils/dateFormat';
import { getApiUrl } from '../../utils/apiUrl';
import { SELECTED_APP_ID_KEY, SELECTED_APP_NAME_KEY } from '../shared/SelectAppPage';

const NotificationsPage = () => {
  const { currentUser } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const showTypeField = currentUser?.group === USER_GROUP.STAFF && currentUser?.department === 'cooperative';
  const [tablePage, setTablePage] = useState(0);
  const [tableRowsPerPage, setTableRowsPerPage] = useState(10);
  const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState(false);
  const [notificationToDeleteId, setNotificationToDeleteId] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState({ severity: 'info', title: '', text: '' });
  const [announcementsList, setAnnouncementsList] = useState([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apps, setApps] = useState([]);
  const [, setAppsLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [displayAppName, setDisplayAppName] = useState(null);
  const [authVerifyUser, setAuthVerifyUser] = useState(null);
  const [authVerifyLoading, setAuthVerifyLoading] = useState(true);
  const [filterSearch, setFilterSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const announcementsUrl = getApiUrl('/api/clients/announcements/');
  const attachmentsUrl = getApiUrl('/api/clients/announcement-attachments/');
  const appsUrl = getApiUrl('/api/clients/apps/').replace(/\/$/, '');
  const deptUrl = getApiUrl('/api/clients/departments/');
  const selectedAppId = typeof localStorage !== 'undefined' ? localStorage.getItem(SELECTED_APP_ID_KEY) : null;

  const getAnnouncementsListUrl = useCallback(() => {
    if (!appsUrl || !selectedAppId) return null;
    return `${appsUrl}/${selectedAppId}/announcements/`;
  }, [appsUrl, selectedAppId]);

  const fetchAnnouncementsList = useCallback(() => {
    const url = getAnnouncementsListUrl();
    if (!url) return Promise.resolve();
    return axios.get(url)
      .then(({ data }) => {
        const list = data?.results ?? (Array.isArray(data) ? data : []);
        setAnnouncementsList(list);
      })
      .catch(() => setAnnouncementsList([]));
  }, [getAnnouncementsListUrl]);

  useEffect(() => {
    if (!getAnnouncementsListUrl()) {
      setAnnouncementsList([]);
      setAnnouncementsLoading(false);
      return;
    }
    setAnnouncementsLoading(true);
    fetchAnnouncementsList()
      .finally(() => setAnnouncementsLoading(false));
  }, [getAnnouncementsListUrl, fetchAnnouncementsList]);

  // โหลดชื่อแอปสำหรับแสดง (จาก localStorage หรือ API) — แบบเดียวกับ ServiceProviderDashboard
  useEffect(() => {
    const appId = typeof localStorage !== 'undefined' ? localStorage.getItem(SELECTED_APP_ID_KEY) : null;
    const fromStorage = typeof localStorage !== 'undefined' ? localStorage.getItem(SELECTED_APP_NAME_KEY) : null;
    if (fromStorage) {
      setDisplayAppName(fromStorage);
      return;
    }
    if (!appId || !appsUrl) {
      setDisplayAppName(null);
      return;
    }
    const base = appsUrl.replace(/\/$/, '');
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    const headers = token ? { Authorization: `Token ${token}` } : {};
    axios.get(`${base}/${appId}/`, { headers })
      .then(({ data }) => {
        const name = data?.name || '';
        setDisplayAppName(name);
        if (name && typeof localStorage !== 'undefined') localStorage.setItem(SELECTED_APP_NAME_KEY, name);
      })
      .catch(() => setDisplayAppName(null));
  }, [appsUrl]);

  // GET auth/verify สำหรับแสดงชื่อผู้ใช้ — แบบเดียวกับ ServiceProviderDashboard
  useEffect(() => {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) {
      setAuthVerifyLoading(false);
      return;
    }
    setAuthVerifyLoading(true);
    const url = getApiUrl('/api/user/auth/verify/');
    axios.get(url, { headers: { Authorization: `Token ${token}` } })
      .then(({ data }) => setAuthVerifyUser(data))
      .catch(() => setAuthVerifyUser(null))
      .finally(() => setAuthVerifyLoading(false));
  }, []);

  useEffect(() => {
    if (!appsUrl) return;
    setAppsLoading(true);
    axios.get(appsUrl)
      .then(({ data }) => {
        const list = data?.results ?? (Array.isArray(data) ? data : []);
        setApps(list);
      })
      .catch(() => setApps([]))
      .finally(() => setAppsLoading(false));
  }, [appsUrl]);

  // โหลด departments จาก apps/{selectedAppId}/departments/ เหมือน ReporterDashboard (scoped by app)
  useEffect(() => {
    const appId = typeof localStorage !== 'undefined' ? localStorage.getItem(SELECTED_APP_ID_KEY) : null;
    const base = appsUrl ? appsUrl.replace(/\/$/, '') : null;
    if (!appId || !base) {
      if (deptUrl) {
        setDepartmentsLoading(true);
        axios.get(deptUrl)
          .then(({ data }) => {
            const list = data?.results ?? (Array.isArray(data) ? data : []);
            setDepartments(list);
          })
          .catch(() => setDepartments([]))
          .finally(() => setDepartmentsLoading(false));
      } else {
        setDepartments([]);
      }
      return;
    }
    setDepartmentsLoading(true);
    const url = `${base}/${appId}/departments/`;
    axios.get(url)
      .then(({ data }) => {
        const list = data?.results ?? (Array.isArray(data) ? data : []);
        setDepartments(list.map((d) => ({ id: d.id, name: d.name || '', description: d.description || '' })));
      })
      .catch(() => setDepartments([]))
      .finally(() => setDepartmentsLoading(false));
  }, [deptUrl, appsUrl, selectedAppId]);

  useEffect(() => {
    setTablePage(0);
  }, [announcementsList]);

  // แสดง Snackbar เมื่อกลับจากหน้าสร้าง/แก้ไขพร้อม state
  useEffect(() => {
    const msg = location.state?.message;
    const severity = location.state?.severity || 'success';
    if (msg) {
      setSnackbarMessage({ severity, title: msg, text: '' });
      setSnackbarOpen(true);
      fetchAnnouncementsList();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.message, location.state?.severity, location.pathname, navigate, fetchAnnouncementsList]);

  const mapApiToDisplay = useCallback((a) => {
    const deptName = a.department_name || departments.find((d) => d.id === a.department)?.name || '';
    const type = /ประปา|น้ำ/i.test(deptName) ? 'water_shutdown' : 'electricity_shutdown';
    return {
      id: a.id,
      type,
      message: a.description || a.title || '',
      departmentId: a.department ?? null,
      departmentName: deptName || '-',
      shutdownDate: a.operation_end || a.operation_start || a.display_end || '',
      createdAt: a.created_at,
      operation_start: a.operation_start ?? '',
      operation_end: a.operation_end ?? '',
      display_start: a.display_start ?? '',
      display_end: a.display_end ?? '',
      attachments: (a.attachments || []).map((att) => ({
        id: att.id,
        file: att.file,
        name: (att.file || '').split('/').pop() || `ไฟล์ ${att.id}`,
        mimeType: /\.(png|jpe?g|gif|webp)(\?|$)/i.test(String(att.file || '').split('/').pop() || '') ? 'image/*' : 'application/octet-stream',
      })),
    };
  }, [departments]);

  const userDepartmentIds = useMemo(() => {
    const ids = new Set();
    const addNumber = (v) => {
      const n = Number(v);
      if (!Number.isNaN(n)) ids.add(n);
    };
    const addFromArray = (arr) => {
      if (!Array.isArray(arr)) return;
      arr.forEach((item) => {
        if (item == null) return;
        if (typeof item === 'number' || typeof item === 'string') addNumber(item);
        else if (typeof item === 'object') addNumber(item.id ?? item.department ?? item.department_id);
      });
    };

    addNumber(authVerifyUser?.department);
    addFromArray(authVerifyUser?.departments);
    addFromArray(authVerifyUser?.staff_departments);
    addFromArray(authVerifyUser?.department_ids);
    addFromArray(authVerifyUser?.staff_department_ids);

    return [...ids];
  }, [authVerifyUser]);

  const allowedTypes = useMemo(() => {
    const departmentNameToType = (name) => (/ประปา|น้ำ/i.test(name || '') ? 'water_shutdown' : 'electricity_shutdown');
    if (!userDepartmentIds.length) return ['water_shutdown', 'electricity_shutdown'];
    const types = new Set(
      userDepartmentIds.map((id) => departmentNameToType(departments.find((d) => Number(d.id) === Number(id))?.name || ''))
    );
    return [...types];
  }, [userDepartmentIds, departments]);

  const goToCreatePage = () => navigate('/notifications/create');
  const goToEditPage = (notification) => navigate(`/notifications/edit/${notification?.id ?? ''}`, { state: { notification } });

  const isUpcoming = (dateString) => {
    return new Date(dateString) >= new Date();
  };

  const getTypeLabel = (type) => type === 'water_shutdown' ? 'ประปา' : 'ไฟฟ้า';

  const getTypeChipStyle = (type) => {
    if (type === 'water_shutdown') return { bgcolor: 'rgba(30, 144, 255, 0.10)', color: '#1e90ff', borderColor: 'rgba(30, 144, 255, 0.25)' };
    return { bgcolor: 'rgba(255, 152, 0, 0.10)', color: '#ff9800', borderColor: 'rgba(255, 152, 0, 0.25)' };
  };

  const getTypeIcon = (type) => type === 'water_shutdown' ? <WaterDropIcon /> : <BoltIcon />;

  const getAttachmentIcon = (mimeType) => {
    const mt = String(mimeType || '');
    if (mt.startsWith('image/')) return <ImageIcon sx={{ fontSize: 18 }} />;
    return <AttachFileIcon sx={{ fontSize: 18 }} />;
  };

  const renderAttachmentIndicators = (notification) => {
    const attachments = Array.isArray(notification?.attachments) ? notification.attachments : [];
    const isImage = (a) =>
      String(a?.mimeType || '').startsWith('image/') ||
      /\.(png|jpe?g|gif|webp)(\?|$)/i.test(String(a?.file || '').split('/').pop() || '');
    const imageCount = attachments.filter(isImage).length;
    const fileCount = attachments.filter((a) => !isImage(a)).length;
    const activeColor = '#845EC2';
    const disabledColor = 'rgba(0,0,0,0.26)';

    return (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.6,
          ml: 0.75,
          verticalAlign: 'middle',
          whiteSpace: 'nowrap',
        }}
      >
        <Tooltip title={imageCount ? `มีรูปแนบ (${imageCount})` : 'ไม่มีรูปแนบ'} arrow>
          <Box component="span" sx={{ display: 'inline-flex', color: imageCount ? activeColor : disabledColor }}>
            <ImageIcon sx={{ fontSize: 18 }} />
          </Box>
        </Tooltip>
        <Tooltip title={fileCount ? `มีไฟล์แนบ (${fileCount})` : 'ไม่มีไฟล์แนบ'} arrow>
          <Box component="span" sx={{ display: 'inline-flex', color: fileCount ? activeColor : disabledColor }}>
            <AttachFileIcon sx={{ fontSize: 18 }} />
          </Box>
        </Tooltip>
      </Box>
    );
  };

  const getDepartmentLabel = (department) => {
    const labels = { water: 'ด้านประปา', electricity: 'ด้านไฟฟ้า', cooperative: 'ด้าน อื่นๆ' };
    return labels[department] || '';
  };

  const getDepartmentChipStyle = (department) => {
    if (department === 'water') return { bgcolor: 'rgba(30, 144, 255, 0.10)', color: '#1e90ff', borderColor: 'rgba(30, 144, 255, 0.25)' };
    if (department === 'electricity') return { bgcolor: 'rgba(255, 152, 0, 0.10)', color: '#ff9800', borderColor: 'rgba(255, 152, 0, 0.25)' };
    if (department === 'cooperative') return { bgcolor: 'rgba(132, 94, 194, 0.10)', color: '#845EC2', borderColor: 'rgba(132, 94, 194, 0.25)' };
    return { bgcolor: 'rgba(0, 0, 0, 0.06)', color: 'rgba(0, 0, 0, 0.65)', borderColor: 'rgba(0, 0, 0, 0.12)' };
  };

  const getDepartmentIcon = (department) => {
    if (department === 'water') return <WaterDropIcon />;
    if (department === 'electricity') return <BoltIcon />;
    if (department === 'cooperative') return <GroupIcon />;
    return undefined;
  };

  const sortedNotifications = useMemo(() => {
    const selectedAppNumber = Number(selectedAppId);
    const isStaffUser = Boolean(authVerifyUser?.is_staff);
    const verifyStaffAppIds = Array.isArray(authVerifyUser?.staff_app_ids)
      ? authVerifyUser.staff_app_ids.map((id) => Number(id)).filter((id) => !Number.isNaN(id))
      : [];
    const verifyStaffDepartmentIds = Array.isArray(authVerifyUser?.staff_department_ids)
      ? authVerifyUser.staff_department_ids.map((id) => Number(id)).filter((id) => !Number.isNaN(id))
      : [];

    // For staff users, rely strictly on verify permissions.
    if (isStaffUser) {
      const hasAnyAppScope = verifyStaffAppIds.length > 0;
      const hasAnyDepartmentScope = verifyStaffDepartmentIds.length > 0;
      if (!hasAnyAppScope && !hasAnyDepartmentScope) return [];
      const appAllowed = verifyStaffAppIds.length === 0 || verifyStaffAppIds.includes(selectedAppNumber);
      if (!appAllowed) return [];
    }

    const mapped = (announcementsList || []).map(mapApiToDisplay).filter(Boolean);
    // Staff with app access but no department restriction -> see all departments in that app.
    const departmentScopeIds = isStaffUser
      ? (verifyStaffDepartmentIds.length ? verifyStaffDepartmentIds : [])
      : userDepartmentIds;
    const byDepartment = departmentScopeIds.length
      ? mapped.filter((n) => n.departmentId != null && departmentScopeIds.includes(Number(n.departmentId)))
      : mapped;
    return [...byDepartment].sort((a, b) => {
      const au = isUpcoming(a.shutdownDate);
      const bu = isUpcoming(b.shutdownDate);
      if (au !== bu) return au ? -1 : 1;
      const at = new Date(a.shutdownDate).getTime();
      const bt = new Date(b.shutdownDate).getTime();
      return au ? at - bt : bt - at;
    });
  }, [announcementsList, mapApiToDisplay, userDepartmentIds, authVerifyUser, selectedAppId]);

  const filteredNotifications = useMemo(() => {
    let list = sortedNotifications;
    const q = (filterSearch || '').trim().toLowerCase();
    if (q) {
      list = list.filter((n) => (n.message || '').toLowerCase().includes(q));
    }
    if (filterType) {
      list = list.filter((n) => n.type === filterType);
    }
    if (filterStatus === 'upcoming') {
      list = list.filter((n) => isUpcoming(n.shutdownDate));
    } else if (filterStatus === 'past') {
      list = list.filter((n) => !isUpcoming(n.shutdownDate));
    }
    return list;
  }, [sortedNotifications, filterSearch, filterType, filterStatus]);

  useEffect(() => {
    setTablePage(0);
  }, [filterSearch, filterType, filterStatus]);

  const paginatedNotifications = filteredNotifications.slice(
    tablePage * tableRowsPerPage,
    tablePage * tableRowsPerPage + tableRowsPerPage
  );

  const FILTER_TYPE_OPTIONS = useMemo(() => [
    { value: '', label: 'ประเภท' },
    ...allowedTypes.map((t) => ({ value: t, label: getTypeLabel(t) })),
  ], [allowedTypes]);

  const FILTER_STATUS_OPTIONS = [
    { value: '', label: 'สถานะ' },
    { value: 'upcoming', label: 'กำลังจะมาถึง' },
    { value: 'past', label: 'ผ่านไปแล้ว' },
  ];

  return (
    <Box>
      {/* Header Section — แอป + ยินดีต้อนรับ แบบเดียวกับ ServiceProviderDashboard */}
      <Box sx={{ mb: 4 }}>
        {selectedAppId && (
          <Typography variant="h6" sx={{ color: 'rgba(0, 0, 0, 0.6)', mb: 1.5, fontWeight: 600, textAlign: 'center' }}>
            APP : {displayAppName != null ? (displayAppName || '-') : 'กำลังโหลด...'}
          </Typography>
        )}
        <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.5)', textAlign: 'left', width: { xs: '100%', sm: 'auto' }, fontSize: '0.8125rem' }}>
          {authVerifyLoading ? 'กำลังโหลด...' : (
            <>
              ยินดีต้อนรับ,{' '}
              <Box component="span" sx={{ display: { xs: 'block', sm: 'inline' } }}>
                {authVerifyUser?.full_name || currentUser?.full_name || currentUser?.username || '-'}
              </Box>
            </>
          )}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.25,
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'rgba(0, 0, 0, 0.65)' }}>
            รายการแจ้งเตือน / ประกาศ
          </Typography>
          
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            sx={{
              borderRadius: 2,
              minHeight: { xs: 44, sm: 36 },
              color: '#845EC2',
              borderColor: 'rgba(132, 94, 194, 0.45)',
              bgcolor: 'rgba(255, 255, 255, 0.45)',
              backdropFilter: 'blur(10px)',
              '&:hover': { borderColor: 'rgba(132, 94, 194, 0.65)', bgcolor: 'rgba(255, 255, 255, 0.6)' },
              flex: { xs: '1 1 100%', sm: '0 0 auto' },
            }}
          >
            กลับ
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={goToCreatePage}
            sx={{
              bgcolor: '#845EC2',
              color: 'white',
              borderRadius: 2,
              minHeight: { xs: 44, sm: 36 },
              px: 2,
              '&:hover': { bgcolor: '#6344A4' },
              flex: { xs: '1 1 100%', sm: '0 0 auto' },
            }}
          >
            สร้างประกาศ
          </Button>
        </Box>
      </Box>

      {announcementsLoading && announcementsList.length === 0 ? (
        <Card
          elevation={0}
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(0, 0, 0, 0.05)'
          }}
        >
          <CardContent>
            <Typography align="center" sx={{ color: 'rgba(0, 0, 0, 0.55)' }}>
              กำลังโหลด...
            </Typography>
          </CardContent>
        </Card>
      ) : sortedNotifications.length === 0 ? (
        <Card
          elevation={0}
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(0, 0, 0, 0.05)'
          }}
        >
          <CardContent>
            <Typography align="center" sx={{ color: 'rgba(0, 0, 0, 0.55)' }}>
              ยังไม่มีรายการแจ้งเตือน
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* แถบตัวกรอง: ค้นหา, ประเภท, สถานะ, ล้าง */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'stretch',
              gap: 1.5,
              mb: 2,
              flexWrap: 'wrap',
            }}
          >
            <TextField
              size="small"
              placeholder="ค้นหา เรื่อง, รายละเอียด"
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'rgba(0, 0, 0, 0.4)', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: { xs: '1 1 100%', sm: '1 1 240px' },
                minWidth: { xs: '100%', sm: 200 },
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#fff',
                  borderRadius: 2,
                  '& fieldset': { borderColor: 'rgba(132, 94, 194, 0.35)' },
                  '&:hover fieldset': { borderColor: 'rgba(132, 94, 194, 0.6)' },
                  '&.Mui-focused fieldset': { borderColor: '#845EC2', borderWidth: 1 },
                },
              }}
            />
            <FormControl
              size="small"
              sx={{
                minWidth: { xs: '100%', sm: 140 },
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#fff',
                  borderRadius: 2,
                  '& fieldset': { borderColor: 'rgba(132, 94, 194, 0.35)' },
                  '&:hover fieldset': { borderColor: 'rgba(132, 94, 194, 0.6)' },
                  '&.Mui-focused fieldset': { borderColor: '#845EC2', borderWidth: 1 },
                },
              }}
              variant="outlined"
            >
              <Select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                displayEmpty
                renderValue={(v) => FILTER_TYPE_OPTIONS.find((o) => o.value === v)?.label ?? 'ประเภท'}
                sx={{ fontSize: '0.875rem' }}
              >
                {FILTER_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value || 'all'} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{
                minWidth: { xs: '100%', sm: 160 },
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#fff',
                  borderRadius: 2,
                  '& fieldset': { borderColor: 'rgba(132, 94, 194, 0.35)' },
                  '&:hover fieldset': { borderColor: 'rgba(132, 94, 194, 0.6)' },
                  '&.Mui-focused fieldset': { borderColor: '#845EC2', borderWidth: 1 },
                },
              }}
              variant="outlined"
            >
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                displayEmpty
                renderValue={(v) => FILTER_STATUS_OPTIONS.find((o) => o.value === v)?.label ?? 'สถานะ'}
                sx={{ fontSize: '0.875rem' }}
              >
                {FILTER_STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value || 'all'} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              onClick={() => {
                setFilterSearch('');
                setFilterType('');
                setFilterStatus('');
              }}
              sx={{
                borderRadius: 2,
                color: '#845EC2',
                borderColor: 'rgba(132, 94, 194, 0.5)',
                bgcolor: 'rgba(255, 255, 255, 0.95)',
                minWidth: { xs: '100%', sm: 80 },
                '&:hover': { borderColor: '#845EC2', bgcolor: 'rgba(132, 94, 194, 0.06)' },
              }}
            >
              ล้าง
            </Button>
          </Box>

          {filteredNotifications.length === 0 ? (
            <Card
              elevation={0}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(0, 0, 0, 0.05)'
              }}
            >
              <CardContent>
                <Typography align="center" sx={{ color: 'rgba(0, 0, 0, 0.55)' }}>
                  ไม่มีรายการที่ตรงกับตัวกรอง
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <>
          {/* Table view: md+ (เหมือนหน้าอื่น) */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(0, 0, 0, 0.05)',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ background: 'linear-gradient(to right, rgb(48, 176, 235), rgba(132, 94, 194, 0.8), rgb(143, 212, 244))' }}>
                    <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }} align="center">ลำดับ</TableCell>
                    {showTypeField && (
                      <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>ประเภท</TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>หน่วยงาน</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>ข้อความ</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }} align="center">วันที่สร้าง</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }} align="center">วันปิดบริการ</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }} align="center">สถานะ</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }} align="center">การดำเนินการ</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedNotifications.map((notification, index) => (
                    <TableRow key={notification.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell sx={{ color: 'rgba(0, 0, 0, 0.7)' }} align="center">
                        {tablePage * tableRowsPerPage + index + 1}
                      </TableCell>
                      {showTypeField && (
                        <TableCell>
                          <Chip
                            label={getTypeLabel(notification.type)}
                            size="small"
                            icon={getTypeIcon(notification.type)}
                            sx={{
                              borderRadius: 2,
                              fontWeight: 700,
                              border: '1px solid',
                              '& .MuiChip-icon': { color: 'inherit' },
                              ...getTypeChipStyle(notification.type)
                            }}
                          />
                        </TableCell>
                      )}
                      <TableCell sx={{ color: 'rgba(0, 0, 0, 0.7)' }}>
                        {notification.departmentName || '-'}
                      </TableCell>
                      <TableCell sx={{ color: 'rgba(0, 0, 0, 0.7)' }}>
                        <Box sx={{ color: 'rgba(0, 0, 0, 0.7)', whiteSpace: 'pre-wrap' }}>
                          <Typography component="span" variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.7)' }}>
                            {notification.message}
                          </Typography>
                          {renderAttachmentIndicators(notification)}
                        </Box>
                        {Array.isArray(notification.attachments) && notification.attachments.length > 0 && (
                          <Box sx={{ mt: 1, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                            {notification.attachments.map((a) => (
                              <Tooltip key={a.id || a.name} title={a?.name || 'ไฟล์แนบ'} arrow>
                                <Chip
                                  size="small"
                                  icon={getAttachmentIcon(a?.mimeType)}
                                  label={a?.name || 'ไฟล์แนบ'}
                                  component="a"
                                  clickable
                                  href={a?.dataUrl || a?.file}
                                  download={a?.dataUrl ? (a?.name || 'attachment') : undefined}
                                  target={a?.file && !a?.dataUrl ? '_blank' : undefined}
                                  rel="noopener noreferrer"
                                  sx={{
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    border: '1px solid rgba(0,0,0,0.12)',
                                    bgcolor: 'rgba(255,255,255,0.65)',
                                    '& .MuiChip-icon': { color: 'inherit' },
                                  }}
                                />
                              </Tooltip>
                            ))}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.875rem' }} align="center">
                        {notification.createdAt ? formatDate(notification.createdAt) : '-'}
                      </TableCell>
                      <TableCell sx={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.875rem' }} align="center">
                        {formatDate(notification.shutdownDate)}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={isUpcoming(notification.shutdownDate) ? 'กำลังจะมาถึง' : 'ผ่านไปแล้ว'}
                          color={isUpcoming(notification.shutdownDate) ? 'warning' : 'success'}
                          size="small"
                          sx={{ borderRadius: 2, fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <IconButton
                            size="small"
                            onClick={() => goToEditPage(notification)}
                            sx={{
                              borderRadius: 2,
                              color: 'rgba(249, 176, 7, 0.6)',
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => {
                              setNotificationToDeleteId(notification.id);
                              setOpenDeleteConfirmDialog(true);
                            }}
                            sx={{ borderRadius: 2 }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredNotifications.length}
              page={tablePage}
              onPageChange={(_, newPage) => setTablePage(newPage)}
              rowsPerPage={tableRowsPerPage}
              onRowsPerPageChange={(e) => {
                setTableRowsPerPage(parseInt(e.target.value, 10));
                setTablePage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="จำนวนแถวต่อหน้า:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} จาก ${count}`}
              sx={{
                borderTop: '1px solid rgba(0,0,0,0.08)',
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                borderRadius: '0 0 8px 8px',
                '.MuiTablePagination-toolbar': { flexWrap: 'wrap', gap: 0.5, justifyContent: { xs: 'center', sm: 'flex-end' } }
              }}
            />
          </Box>

          {/* Card view: xs/sm */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            <Grid container spacing={2}>
              {paginatedNotifications.map((notification, index) => (
                <Grid item xs={12} key={notification.id}>
                  <Card
                    elevation={0}
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: 2,
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                      border: '1px solid rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
                        transform: 'translateY(-2px)',
                      }
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                        <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.5)', fontWeight: 600, minWidth: 24 }}>
                          {tablePage * tableRowsPerPage + index + 1}.
                        </Typography>
                        {showTypeField && (
                          <Chip
                            label={getTypeLabel(notification.type)}
                            size="small"
                            icon={getTypeIcon(notification.type)}
                            sx={{
                              borderRadius: 2,
                              fontWeight: 700,
                              border: '1px solid',
                              '& .MuiChip-icon': { color: 'inherit' },
                              ...getTypeChipStyle(notification.type)
                            }}
                          />
                        )}
                        <Chip
                          label={isUpcoming(notification.shutdownDate) ? 'กำลังจะมาถึง' : 'ผ่านไปแล้ว'}
                          color={isUpcoming(notification.shutdownDate) ? 'warning' : 'success'}
                          size="small"
                          sx={{ borderRadius: 2, fontWeight: 600 }}
                        />
                      </Box>

                      <Box sx={{ mb: 1.5, color: 'rgba(0, 0, 0, 0.6)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                        <Typography component="span" variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                          {notification.message}
                        </Typography>
                        {renderAttachmentIndicators(notification)}
                      </Box>
                      {Array.isArray(notification.attachments) && notification.attachments.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
                            {notification.attachments.map((a) => (
                            <Tooltip key={a.id || a.name} title={a?.name || 'ไฟล์แนบ'} arrow>
                              <Chip
                                size="small"
                                icon={getAttachmentIcon(a?.mimeType)}
                                label={a?.name || 'ไฟล์แนบ'}
                                component="a"
                                clickable
                                href={a?.dataUrl || a?.file}
                                download={a?.dataUrl ? (a?.name || 'attachment') : undefined}
                                target={a?.file && !a?.dataUrl ? '_blank' : undefined}
                                rel="noopener noreferrer"
                                sx={{
                                  borderRadius: 2,
                                  fontWeight: 700,
                                  border: '1px solid rgba(0,0,0,0.12)',
                                  bgcolor: 'rgba(255,255,255,0.65)',
                                  '& .MuiChip-icon': { color: 'inherit' },
                                }}
                              />
                            </Tooltip>
                          ))}
                        </Box>
                      )}
                      <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.55)', display: 'block' }}>
                        วันที่สร้าง: {notification.createdAt ? formatDate(notification.createdAt) : '-'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.55)', display: 'block' }}>
                        วันปิดบริการ: {formatDate(notification.shutdownDate)}
                      </Typography>

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: { xs: 'nowrap', sm: 'wrap' }, mt: 2 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={isXs ? <EditIcon fontSize="small" /> : undefined}
                          onClick={() => goToEditPage(notification)}
                          sx={{
                            borderRadius: 1,
                            minWidth: { xs: 0, sm: 40 },
                            px: { xs: 1.25, sm: 1 },
                            minHeight: { xs: 44, sm: 32 },
                            flex: { xs: '1 1 0', sm: '0 0 auto' },
                            color: '#845EC2',
                            borderColor: '#845EC2',
                            '&:hover': { borderColor: '#6344A4', bgcolor: 'rgba(132, 94, 194, 0.08)' }
                          }}
                        >
                          {isXs ? 'แก้ไข' : <EditIcon fontSize="small" />}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={isXs ? <DeleteIcon fontSize="small" /> : undefined}
                          onClick={() => {
                            setNotificationToDeleteId(notification.id);
                            setOpenDeleteConfirmDialog(true);
                          }}
                          sx={{
                            borderRadius: 1,
                            minWidth: { xs: 0, sm: 40 },
                            px: { xs: 1.25, sm: 1 },
                            minHeight: { xs: 44, sm: 32 },
                            flex: { xs: '1 1 0', sm: '0 0 auto' }
                          }}
                        >
                          {isXs ? 'ลบ' : <DeleteIcon fontSize="small" />}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <TablePagination
              component="div"
              count={filteredNotifications.length}
              page={tablePage}
              onPageChange={(_, newPage) => setTablePage(newPage)}
              rowsPerPage={tableRowsPerPage}
              onRowsPerPageChange={(e) => {
                setTableRowsPerPage(parseInt(e.target.value, 10));
                setTablePage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="จำนวนแถวต่อหน้า:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} จาก ${count}`}
              sx={{
                borderTop: '1px solid rgba(0,0,0,0.08)',
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                borderRadius: 2,
                mt: 1,
                '.MuiTablePagination-toolbar': { flexWrap: 'wrap', gap: 0.5, justifyContent: { xs: 'center', sm: 'flex-end' } }
              }}
            />
          </Box>
            </>
          )}
        </>
      )}

      {/* Snackbar แบบเดียวกับหน้าอื่น */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={isXs ? { vertical: 'bottom', horizontal: 'center' } : { vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarMessage.severity}
          sx={{ width: '100%', fontSize: '0.875rem' }}
        >
          {snackbarMessage.title && <AlertTitle sx={{ fontSize: '0.875rem' }}>{snackbarMessage.title}</AlertTitle>}
          {snackbarMessage.text}
        </Alert>
      </Snackbar>

      {/* Dialog ยืนยันลบ (แทน window.confirm) */}
      <Dialog
        open={openDeleteConfirmDialog}
        onClose={() => { setOpenDeleteConfirmDialog(false); setNotificationToDeleteId(null); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>ยืนยันการลบ</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 0.5 }}>
            <AlertTitle sx={{ fontSize: '0.9375rem' }}>ต้องการลบรายการนี้ใช่หรือไม่?</AlertTitle>
            การลบไม่สามารถกู้คืนได้
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setOpenDeleteConfirmDialog(false); setNotificationToDeleteId(null); }} sx={{ borderRadius: 1 }}>
            ยกเลิก
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              const deleteUrl = notificationToDeleteId
                ? `${(announcementsUrl || '/api/clients/announcements/').replace(/\/$/, '')}/${notificationToDeleteId}/`
                : null;
              if (deleteUrl) {
                try {
                  await axios.delete(deleteUrl);
                  setSnackbarMessage({ severity: 'success', title: 'ลบแล้ว', text: 'ลบรายการเรียบร้อย' });
                  setSnackbarOpen(true);
                  await fetchAnnouncementsList();
                } catch (err) {
                  setSnackbarMessage({ severity: 'error', title: 'ลบไม่สำเร็จ', text: err?.response?.data?.detail || err?.message || '' });
                  setSnackbarOpen(true);
                }
                setOpenDeleteConfirmDialog(false);
                setNotificationToDeleteId(null);
              }
            }}
            sx={{ borderRadius: 1 }}
          >
            ลบ
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default NotificationsPage;
